import { AnyAction } from "redux";
import { IFlightError } from "@vmosolutions/oc-interfaces";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { IRootState } from "../../reducers/CombinedReducers";
import { Auth, API } from "aws-amplify";

export enum Type {
  SET_FLIGHT_SCANNER_RUNNING = "SET_FLIGHT_SCANNER_RUNNING",
  GOT_FLIGHT_SCANNER_RESULTS = "GOT_FLIGHT_SCANNER_RESULTS",
  GOT_FLIGHT_ERRORS = "GOT_FLIGHT_ERRORS",
}

export interface ISetFlightScannerRunning extends AnyAction {
  type: Type.SET_FLIGHT_SCANNER_RUNNING;
  isFlightScannerFinished: boolean;
}

export interface IGotFlightScannerResults extends AnyAction {
  type: Type.GOT_FLIGHT_SCANNER_RESULTS;
  flightErrors: IFlightError[];
}

export interface IGotFlightErrors extends AnyAction {
  type: Type.GOT_FLIGHT_ERRORS;
  newErrors: IFlightError[];
  fixedErrors: IFlightError[];
}

export type Action = ISetFlightScannerRunning | IGotFlightScannerResults | IGotFlightErrors;

/**
 * getFlightErrorsAction Wrapper function to dispatch the redux action to get the flight errors
 */
export const getFlightErrorsAction = (): ((dispatch: ThunkDispatch<IRootState, object, Action>) => Promise<void>) =>
  getFlightErrors;

/**
 * getFlightErrors a function that retrieves all the flight errors from the database
 * @param dispatch - A ThunkDispatch taking the root state, and the dispatch action IGotFlightScannerResults
 * call this to dispatch action after we have gotten the flight errors
 */
const getFlightErrors = async (dispatch: ThunkDispatch<IRootState, object, Action>): Promise<void> => {
  const userSession = await Auth.currentSession();
  const token = userSession.getIdToken().getJwtToken();
  const response = await API.get("skytrains", "/flightErrors", {
    headers: { Accept: "*/*", Authorization: token },
  });

  if (!response) return;

  const { flightErrors } = response;
  flightErrors.sort((a: IFlightError, b: IFlightError) => (a.priority > b.priority ? 1 : -1));
  dispatch({
    type: Type.SET_FLIGHT_SCANNER_RUNNING,
    isFlightScannerFinished: true,
  });
  dispatch({
    type: Type.GOT_FLIGHT_SCANNER_RESULTS,
    flightErrors,
  });
};

type GetFlightScannerResults = ThunkAction<Promise<void>, IRootState, object, IGotFlightScannerResults>;

/**
 * webSocketUpdatedFlightScannerResults: A ThunkAction which is an asynchronous function to set the state
 * of the flight error scanner lambda function
 * @param isFlightScannerFinished - Boolean, Whether or not the the flight scanner has finished
 */
export const webSocketUpdatedFlightScannerResults = (
  isFlightScannerFinished: boolean
): GetFlightScannerResults | ISetFlightScannerRunning => {
  if (isFlightScannerFinished) {
    return getFlightErrors;
  }

  return { type: Type.SET_FLIGHT_SCANNER_RUNNING, isFlightScannerFinished };
};

export const websocketUpdatedFlightErrors = (
  newErrors: IFlightError[],
  fixedErrors: IFlightError[]
): IGotFlightErrors => {
  return {
    type: Type.GOT_FLIGHT_ERRORS,
    newErrors,
    fixedErrors,
  };
};
